import dayjs from 'dayjs';
import { cn } from '@noor/lib/cn';
import { merchantChatFileUrl, type MerchantChatMessage } from '@/entities/merchant-chat';

/**
 * One balloon — text or a 220px image attachment, outgoing right on brand,
 * incoming left on muted, timestamp tucked bottom-right. Either an image or
 * text renders, never both: the composer only ever attaches to an empty draft.
 */
export function MerchantMessageBubble({
  message,
  onImageOpen,
}: {
  message: MerchantChatMessage;
  onImageOpen: (messageId: number) => void;
}) {
  const firstFile = message.files?.[0] ?? null;
  const imageUrl = merchantChatFileUrl(firstFile);
  const time = dayjs(message.created_at).format('HH:mm');

  return (
    <div
      className={cn(
        'mb-1 flex',
        firstFile ? 'items-start' : 'items-center',
        message.outgoing ? 'justify-end' : 'justify-start',
      )}
    >
      <div className="relative w-[312px] max-w-full">
        <div
          className={cn(
            'relative mt-1 overflow-hidden rounded-xl',
            message.outgoing
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground',
            firstFile && imageUrl ? 'p-0' : 'p-2.5 pb-[34px]',
          )}
        >
          {firstFile && imageUrl ? (
            <button
              type="button"
              onClick={() => onImageOpen(message.id)}
              className="block w-full cursor-pointer outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
              aria-label={dayjs(message.created_at).format('HH:mm')}
            >
              <img
                src={imageUrl}
                alt=""
                className="h-[220px] w-full rounded-xl object-cover"
                loading="lazy"
              />
            </button>
          ) : (
            <p className="min-w-12 text-base leading-[19px] font-medium break-words">
              {message.message}
            </p>
          )}
          <span
            className={cn(
              'absolute right-2 bottom-1 text-xs leading-[14px] font-medium',
              message.outgoing ? 'text-primary-foreground' : 'text-muted-foreground',
            )}
          >
            {time}
          </span>
        </div>
      </div>
    </div>
  );
}
